import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTabContext } from 'context/TabContext';
import { useLoading } from 'hooks/useLoading';
import { Box, Text, Button } from '@chakra-ui/react';
import { format } from 'date-fns';
import Heading from 'components/Heading/Heading';
import Checkbox from 'components/Checkbox/Checkbox';
import { post } from 'api/httpClient';
import { useSession } from 'context/SessionContext';
import { useUserSession } from 'context/UserContext';
import { Activities, Section } from 'types';
import SectionComponent from 'components/SectionComponent/SectionComponent';
import { useQuestionnaire } from 'hooks/useQuestionnaire';
import { fetchExtraQuestions } from 'hooks/useFetchQuestions';
import Subtitle from 'components/Subtitle/Subtitle';


const ProfessionField: React.FC<{
  userData: any;
  extraData: Section[];
  activities: Activities[];
}> = ({ userData, extraData, activities }) => {
  let userActivity = activities.find(a => a.code === userData?.professionCode);
  let hasExtraQuestions = extraData.some(section => 
    section.items.some(item => item.type !== 'Questionário')
  );
  
  // Extra questions already bring the profession information
  if (hasExtraQuestions) return null;

  return (
    <Box display="flex" gap="15px" mb={3}>
      <Text fontSize="md" color="brand.fontColors.third" textAlign={['left']} fontWeight={500}>
        Profissão: {userActivity?.description}
      </Text>
    </Box>
  );
};

const Profession: React.FC = () => {
  const { completeTab, activeTab, loading, setLoading } = useTabContext();
  const { startLoading, stopLoading, LoadingSpinner } = useLoading();
  const { userData } = useUserSession();
  const [extraData, setExtraData] = useState<Section[]>([]);
  const { data, saveQuestionnaire, activities, setActivities } = useSession();
  const { validateInputs, validateSubQuestion, mapQuestions } = useQuestionnaire();
  const {
    control,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm();


  useEffect(() => {
    const fetchQuestions = async () => {
      startLoading();
      try { 
        const [activitiesResponse, extraQuestionsResponse] = await Promise.all([
          post('/v1/Questionnaire/GetActivities', { activityCode: 'ALL' }),
          fetchExtraQuestions([{
             code: userData?.professionCode,
             activity: 'P',
           }])
        ]);
        setExtraData(extraQuestionsResponse.result.sections);
        setActivities(activitiesResponse.result.activities);
      } catch (error: any) {
      } finally {
        stopLoading();
      }
    };

    fetchQuestions();
  }, []);


  const handleNext = async (values: any) => {
    setLoading(true);
    
    const mappedQuestions = mapQuestions(extraData, getValues());

    saveQuestionnaire(null as any, mappedQuestions);

    try {
      completeTab(activeTab);
    } catch (error) {
      console.error('Erro ao enviar dados:', error);
    } finally {
      setLoading(false);
    }
  };


  function consentsText(){
    let lines = userData?.additionalInformations.find((x: any) => x.code === 'CONSENTIMENTO_DADOS_CLINICOS');

    return (
      <>
        <Subtitle key={lines.title} text={lines.title} format={"0"} />
        {
          lines.descriptions.map((element:any, i:number) => (
           
            <Subtitle key={element.value} text={element.value} format={"5"}/>
          ))
        }
      </>
    )
  }

  return (
    <Box>
      <Box>
        <Heading title="Confirmar Dados Interveniente" />
      </Box>
      <Box>
        <Box display="flex" gap="15px" mb={3} mt={10}>
          <Text
            fontSize="md"
            color="brand.fontColors.primary"
            textAlign={['left']}
            fontWeight={500}
          >
            Nome
          </Text>
          <Text fontSize="md" color="brand.fontColors.primary">
            {userData?.name}
          </Text>
        </Box>

        <Box 
          display="flex" 
          flexDirection={['column', 'row']} 
          gap={[3, '50px']} 
          mb={3}
        >
          <Box display="flex" gap="15px">
            <Text
              fontSize="md"
              color="brand.fontColors.primary"
              textAlign={['left']}
              fontWeight={500}
            >
              Data Nascimento
            </Text>
            <Text fontSize="md" color="brand.fontColors.primary">
            {userData?.birthDate ? format(new Date(userData.birthDate), 'dd/MM/yyyy') : ''}
            </Text>
          </Box>
          <Box display="flex" gap="15px">
            <Text
              fontSize="md"
              color="brand.fontColors.primary"
              textAlign={['left']}
              fontWeight={500}
            >
              Idade Atuarial
            </Text>
            <Text fontSize="md" color="brand.fontColors.primary">
              {userData?.actuarialAge}
            </Text>
          </Box>
        </Box>
        <Box display="flex" gap="15px" mb={5}>
          <Text
            fontSize="md"
            color="brand.fontColors.primary"
            textAlign={['left']}
            fontWeight={500}
          >
            NIF
          </Text>
          <Text fontSize="md" color="brand.fontColors.primary">
            {userData?.nif}
          </Text>
        </Box>
        <form onSubmit={handleSubmit(handleNext)}>
          <ProfessionField
            userData={userData}
            extraData={extraData}
            activities={activities}
          />
          <Box>
            {extraData.map(extraDataList => (
              <SectionComponent
                key="extraDataList"
                sectionList={extraDataList}
                control={control}
                validateInputs={validateInputs}
                getValues={getValues}
                validateSubQuestion={validateSubQuestion}
                errors={errors}
              />
            ))}
          </Box>

          {consentsText()}

          <Checkbox
            name="confirmInfo"
            control={control}
            label="Confirmo a Informação"
            rules={{ required: 'Campo de preenchimento obrigatório' }}
            error={errors?.['confirmInfo']}
          />

          <Box display="flex" alignItems="center" justifyContent="flex-end">
            <Button
              variant="primary"
              type="submit"
              isLoading={loading}
              loadingText="A Enviar"
            >
              Continuar
            </Button>
          </Box>
        </form>
      </Box>
      <LoadingSpinner />
    </Box>
  );
};

export default Profession;
